import { createBrowserRouter, Navigate, type RouteObject } from 'react-router-dom';
import { AppShell } from '@/components/layout/AppShell';
import { ErrorBoundary } from '@/components/feedback/ErrorBoundary';
import { PlaceholderPage } from '@/components/feedback/PlaceholderPage';
import { ProtectedRoute } from '@/auth/ProtectedRoute';
import { LoginPage } from '@/auth/LoginPage';
import { CallbackPage } from '@/auth/CallbackPage';
import { paths } from './paths';

import { batteryModelRoutes } from '@/modules/batteryModels/routes';
import { dynamicRecordRoutes } from '@/modules/dynamicRecords/routes';
import { workflowRoutes } from '@/modules/workflow/routes';
import { registryRoutes } from '@/modules/registry/routes';
import { dashboardRoutes } from '@/modules/dashboard/routes';
import { customerRoutes } from '@/modules/customers/routes';
import { quotationRoutes } from '@/modules/quotations/routes';
import { salesOrderRoutes } from '@/modules/salesOrders/routes';

/**
 * Routes for backend modules whose UI hasn't been implemented yet. Each lands
 * on a {@link PlaceholderPage} so the nav drawer stays functional and the
 * structure stays visible. Replace these one at a time as the modules ship.
 */
const placeholderRoutes: RouteObject[] = [
  { path: paths.workOrders, element: <PlaceholderPage title="Work orders" /> },
  { path: paths.shipments, element: <PlaceholderPage title="Shipments" /> },
  { path: paths.warranty, element: <PlaceholderPage title="Warranty" /> },
  { path: paths.serviceCases, element: <PlaceholderPage title="Service cases" /> },
  { path: paths.metadata, element: <PlaceholderPage title="Metadata definitions" /> },
  { path: paths.attachments, element: <PlaceholderPage title="Attachments" /> }
];

/**
 * Authenticated section — wrapped in {@link AppShell} so the drawer + topbar
 * are always visible and protected by {@link ProtectedRoute}. Children inherit
 * the protection.
 */
const authenticatedRoutes: RouteObject = {
  element: (
    <ProtectedRoute>
      <AppShell />
    </ProtectedRoute>
  ),
  errorElement: <ErrorBoundary><div /></ErrorBoundary>,
  children: [
    // Post-login landing — Dashboard. `/` inside AppShell redirects here.
    { index: true, element: <Navigate to={paths.dashboard} replace /> },
    ...dashboardRoutes,
    ...customerRoutes,
    ...batteryModelRoutes,
    ...quotationRoutes,
    ...salesOrderRoutes,
    ...dynamicRecordRoutes,
    ...workflowRoutes,
    // Registry admin tooling lives under /registry. The public passport route
    // (also exported by registryRoutes) is mounted separately below so it
    // bypasses ProtectedRoute + AppShell.
    ...registryRoutes.filter((r) => r.path !== paths.passport),
    ...placeholderRoutes
  ]
};

/** OIDC sign-in trigger — auto-redirects the browser to Keycloak. */
const loginRoute: RouteObject = {
  path: paths.login,
  element: <LoginPage />
};

/** OIDC redirect target — completes the code exchange and bounces to `state.from`. */
const callbackRoute: RouteObject = {
  path: paths.callback,
  element: <CallbackPage />
};

const forbiddenRoute: RouteObject = {
  path: paths.forbidden,
  element: (
    <PlaceholderPage
      title="Forbidden"
      description="Your session does not have the scope required for this page."
    />
  )
};

/** Public — no AppShell, no auth. Used by QR-scan landings. */
const publicPassportRoute = registryRoutes.find((r) => r.path === paths.passport)!;

export const router = createBrowserRouter([
  publicPassportRoute,
  loginRoute,
  callbackRoute,
  forbiddenRoute,
  authenticatedRoutes
]);
